import { uuid as uuid } from 'vue3-uuid'; // Import the uuid function
import {defineStore} from 'pinia';
import {effectScope, watch} from 'vue';
import {useSecurityStore} from './dsecurity';
import {reconnectDelay, socketParamsKey} from '../helpers/dreconnect.mjs';

// The one socket. Events from a socket that has been replaced are ignored, so there is never more than one live.
let webSocket = null;
let webSocketParams = null; // the custom parameters webSocket was opened with
let webSocketCreatedAt = 0;
let websocketRetryTimeout = null;
let retryAttempt = 0;
let resumeTimeout = null;
let hiddenSince = null;
let missedWhileHidden = false;
let lifecycleInstalled = false;

var project_name = process.env.VUE_APP_PROJECT_NAME;
const websocket_localstorage_key = project_name + "_websocket";

// a socket that stayed open this long was fine: the next drop starts the backoff from the beginning
const STABLE_AFTER_MS = 10000;
// back from the background after this long: the socket may be dead without knowing it (iOS), so open a new one
const STALE_AFTER_HIDDEN_MS = 10000;
// several resume signals at once (visible, pageshow, online, the new socket opening) give one re-fetch
const RESUME_DEBOUNCE_MS = 500;
// a socket younger than this is not replaced on resume (visible, pageshow and online often come together)
const FRESH_SOCKET_MS = 2000;

export const useWebSocketStore = defineStore("websocket", {
    state: () => ({
        id: null,
        connected: false,
        connecting: false,
        keepid: true,
        customparamas: {}, // { key: value}
        message: null, // { type: null, data: null}
        // bumped when messages may have been missed (the socket was re-opened, the app came back from the
        // background): whoever shows server state watches it and fetches the state again
        resumed: 0,
    }),

    actions: {
        async connect() {
            installLifecycle(this);
            const secStore = useSecurityStore();

            if (!secStore.token) {
                // logged out: no socket and no retries until someone logs in
                this.disconnect();
                return;
            }

            if (webSocket && (webSocket.readyState === WebSocket.OPEN || webSocket.readyState === WebSocket.CONNECTING)) {
                // one socket at a time
                return;
            }

            if (!("WebSocket" in window)) {
                // The browser doesn't support WebSocket
                console.error("WebSocket NOT supported by your Browser!");
                return;
            }

            clearTimeout(websocketRetryTimeout);
            websocketRetryTimeout = null;

            // Check if browser can detect internet connection
            if (window.navigator.onLine === false) {
                // no internet: try again later, or at once when the browser says it is back (online event)
                scheduleReconnect(this);
                return;
            }

            openSocket(this, secStore.token);
        },
        async reconnect() {
            // a board wants a socket with its parameters (PlayerGame): keep the one we have if it already has them
            if (webSocket && webSocketParams === socketParamsKey(this.customparamas)
                && (webSocket.readyState === WebSocket.OPEN || webSocket.readyState === WebSocket.CONNECTING))
                return;
            closeSocket(this);
            retryAttempt = 0;
            this.connect();
        },
        /** Logout: close the socket and stop reconnecting. */
        disconnect() {
            closeSocket(this);
            retryAttempt = 0;
            clearTimeout(resumeTimeout);
            resumeTimeout = null;
            missedWhileHidden = false;
        },
    }
})

function openSocket(store, token) {
    // try get Id
    var storage_id = localStorage.getItem(websocket_localstorage_key);
    if(storage_id && store.keepid)
        store.id = storage_id;
    else {
        store.id = uuid.v4();
        localStorage.setItem(websocket_localstorage_key, store.id);
    }

    let websocketUrl = process.env.VUE_APP_WEBSOCKET_URL + "?id=" + store.id + "&AuthorizationToken=" + token;
    for(var key in store.customparamas){
        websocketUrl += "&" + key + "=" + store.customparamas[key];
    }

    let socket;
    try {
        socket = new WebSocket(websocketUrl);
    }
    catch (error) {
        console.error("Websocket could not be created", error);
        scheduleReconnect(store);
        return;
    }
    webSocket = socket;
    webSocketParams = socketParamsKey(store.customparamas);
    webSocketCreatedAt = Date.now();
    store.connected = false;
    store.connecting = true;
    let openedAt = 0;

    socket.onopen = function () {
        if (socket !== webSocket)
            return;
        openedAt = Date.now();
        store.connected = true;
        store.connecting = false;
        // whatever was sent while there was no socket (or the old one was for another game) never reached us
        signalResume(store);
    };

    socket.onclose = function () {
        if (socket !== webSocket)
            return; // a socket we replaced or closed on purpose
        webSocket = null;
        webSocketParams = null;
        store.connected = false;
        store.connecting = false;
        if (openedAt && Date.now() - openedAt >= STABLE_AFTER_MS)
            retryAttempt = 0;
        // dropped (idle timeout, network change, iOS put the app in the background): connect again
        scheduleReconnect(store);
    };

    socket.onerror = function (event) {
        if (socket === webSocket)
            console.error("Websocket error->", event);
    };

    socket.onmessage = function (event) {
        if (socket !== webSocket)
            return;
        try {
            store.message = JSON.parse(event.data);
        }
        catch (error) {
            console.error("Websocket message is not JSON", error);
        }
    };
}

function closeSocket(store) {
    clearTimeout(websocketRetryTimeout);
    websocketRetryTimeout = null;
    if (webSocket) {
        const socket = webSocket;
        webSocket = null;
        webSocketParams = null;
        socket.onopen = socket.onclose = socket.onerror = socket.onmessage = null;
        try {
            socket.close();
        }
        catch {
            // already closing
        }
    }
    store.connected = false;
    store.connecting = false;
}

function scheduleReconnect(store) {
    clearTimeout(websocketRetryTimeout);
    websocketRetryTimeout = null;
    if (!useSecurityStore().token)
        return; // logged out: stay closed
    const delay = reconnectDelay(retryAttempt);
    retryAttempt++;
    websocketRetryTimeout = setTimeout(() => {
        websocketRetryTimeout = null;
        store.connect();
    }, delay);
}

function signalResume(store) {
    if (document.visibilityState === "hidden") {
        // nobody looks now; the state is fetched when the app is visible again
        missedWhileHidden = true;
        return;
    }
    clearTimeout(resumeTimeout);
    resumeTimeout = setTimeout(() => {
        resumeTimeout = null;
        store.resumed++;
    }, RESUME_DEBOUNCE_MS);
}

/** Back from the background (long), from the back/forward cache or from offline: a new socket and fresh state. */
function resume(store) {
    if (!useSecurityStore().token)
        return;
    missedWhileHidden = false;
    signalResume(store); // also when the socket cannot be opened: the state comes over HTTP
    if (webSocket && Date.now() - webSocketCreatedAt < FRESH_SOCKET_MS)
        return; // just opened by the signal before this one
    closeSocket(store);
    retryAttempt = 0;
    store.connect();
}

function installLifecycle(store) {
    if (lifecycleInstalled || typeof window === "undefined")
        return;
    lifecycleInstalled = true;

    // login opens the socket, logout closes it for good and forgets its parameters (the last game's PlayerGame
    // belongs to the user who logged out). A detached scope: this may first run inside a component.
    const secStore = useSecurityStore();
    effectScope(true).run(() => {
        watch(() => secStore.token, (token, previous) => {
            if (previous) {
                store.disconnect();
                store.customparamas = {};
            }
            if (token)
                store.connect();
        });
    });

    document.addEventListener("visibilitychange", () => {
        if (document.visibilityState === "hidden") {
            hiddenSince = Date.now();
            return;
        }
        const away = hiddenSince ? Date.now() - hiddenSince : 0;
        hiddenSince = null;
        if (away >= STALE_AFTER_HIDDEN_MS) {
            resume(store);
            return;
        }
        // a short look elsewhere: keep a live socket, open one now if it dropped
        if (missedWhileHidden) {
            missedWhileHidden = false;
            signalResume(store);
        }
        retryAttempt = 0;
        store.connect();
    });
    window.addEventListener("online", () => resume(store));
    window.addEventListener("pageshow", (event) => {
        if (event.persisted)
            resume(store);
    });
}
